import { STATUS } from "@/constants/statuses";
import RewardsSpendingRulesStore from "@apps/rewards/modules/SpendingRules/store/RewardsSpendingRulesStore";
import { rewardsApplicationLoggerToken } from "@apps/rewards/tokens";
import { getLoadingStatus } from "@apps/rewards/utils/statuses";
import { applyTransaction } from "@datorama/akita";
import type { Observable } from "rxjs";
import { catchError, finalize, lastValueFrom, tap } from "rxjs";
import { inject, singleton } from "tsyringe";

import type { LoggerInterface } from "@interfaces/LoggerInterface";

import RewardsCustomerRewardsDiscountService from "../../CustomerRewards/services/RewardsCustomerRewardsDiscountService";
import RewardsSpendingRulesApiService from "../api_service/RewardsSpendingRulesApiService";
import { SPENDING_RULES_REDEEM_REWARD_TYPES } from "../constants";
import type {
    SpendingRulesRedeemEntity,
    SpendingRulesRefundResponse,
    SpendingRulesResponse,
} from "../entities/RewardsSpendingRulesEntities";
import type {
    SpendingRulesApplyDiscountParams,
    SpendingRulesQueryParams,
    SpendingRulesRedeemBody,
    SpendingRulesRefundBody,
} from "../types";

@singleton()
class RewardsSpendingRulesService {
    constructor(
        @inject(rewardsApplicationLoggerToken)
        private readonly logger: LoggerInterface,
        private readonly spendingRulesApiService: RewardsSpendingRulesApiService,
        private readonly spendingRulesStore: RewardsSpendingRulesStore,
        private readonly customerRewardsDiscountService: RewardsCustomerRewardsDiscountService
    ) {}

    public getSpendingRules(
        queryParams: SpendingRulesQueryParams
    ): Observable<SpendingRulesResponse> {
        this.logger.debug(
            "RewardsSpendingRulesService.getSpendingRules is started"
        );

        const status = this.spendingRulesStore.getStatus();
        this.spendingRulesStore.setStatus(getLoadingStatus(status));

        return this.spendingRulesApiService.getSpendingRules(queryParams).pipe(
            catchError((error: unknown) => {
                this.logger.debug(
                    "RewardsSpendingRulesService.getSpendingRules is errored",
                    error
                );
                this.spendingRulesStore.setStatus(STATUS.ERROR);
                throw error;
            }),
            tap((value) => {
                this.logger.debug(
                    "RewardsSpendingRulesService.getSpendingRules is ready"
                );
                //Store Update
                applyTransaction(() => {
                    this.spendingRulesStore.update((state) => ({
                        ...state,
                        spendingRulesList: value.items,
                    }));
                    this.spendingRulesStore.setStatus(STATUS.READY);
                });
            })
        );
    }

    private redeem(
        body: SpendingRulesRedeemBody
    ): Observable<SpendingRulesRedeemEntity> {
        this.logger.debug("RewardsSpendingRulesService.redeem is started");

        this.spendingRulesStore.setLoading(true);

        return this.spendingRulesApiService.redeem(body).pipe(
            finalize(() => {
                this.spendingRulesStore.setLoading(false);
            }),
            catchError((error: unknown) => {
                this.logger.debug(
                    "RewardsSpendingRulesService.redeem is errored",
                    { body, error }
                );
                this.spendingRulesStore.setError(true);
                throw error;
            }),
            tap(() => {
                this.logger.debug(
                    "RewardsSpendingRulesService.redeem is ready",
                    body
                );
            })
        );
    }

    public redeemStoreCredit(
        body: SpendingRulesRedeemBody
    ): Observable<{ storeCreditAmount: number }> {
        this.logger.debug(
            "RewardsSpendingRulesService.redeemStoreCredit is started"
        );

        this.spendingRulesStore.setLoading(true);

        return this.spendingRulesApiService.redeemStoreCredit(body).pipe(
            finalize(() => {
                this.spendingRulesStore.setLoading(false);
            }),
            catchError((error: unknown) => {
                this.logger.debug(
                    "RewardsSpendingRulesService.redeemStoreCredit is errored",
                    { body, error }
                );
                throw error;
            }),
            tap(() => {
                this.logger.debug(
                    "RewardsSpendingRulesService.redeemStoreCredit is ready",
                    body
                );
            })
        );
    }

    private applyDiscount({
        rewardType,
        rewardCode,
        rewardId,
    }: SpendingRulesApplyDiscountParams) {
        const { amountDiscount, percentageDiscount, shippingDiscount } =
            SPENDING_RULES_REDEEM_REWARD_TYPES;

        if (
            rewardType === amountDiscount ||
            rewardType === percentageDiscount ||
            rewardType === shippingDiscount
        ) {
            void this.customerRewardsDiscountService.applyDiscount(
                rewardCode,
                rewardId
            );
        }
    }

    public async redeemAndApplyDiscount(
        body: SpendingRulesRedeemBody
    ): Promise<SpendingRulesRedeemEntity> {
        const response = await lastValueFrom(this.redeem(body));

        this.applyDiscount({
            rewardId: response.rewardId,
            rewardCode: response.rewardCode,
            rewardType: response.rewardType,
        });

        return response;
    }

    //Use this method when two or more widgets can be injected on the same page to avoid multiple requests
    public async checkRequestStatusAndGetSpendingRules(
        queryParams: SpendingRulesQueryParams
    ): Promise<void> {
        if (this.spendingRulesStore.getStatus() === STATUS.VIRGIN)
            await lastValueFrom(this.getSpendingRules(queryParams));
    }

    public refund(
        body: SpendingRulesRefundBody
    ): Observable<SpendingRulesRefundResponse> {
        this.logger.debug("RewardsSpendingRulesService.refund is started");

        this.spendingRulesStore.setLoading(true);

        return this.spendingRulesApiService.refund(body).pipe(
            finalize(() => {
                this.spendingRulesStore.setLoading(false);
            }),
            catchError((error: unknown) => {
                this.logger.debug(
                    "RewardsSpendingRulesService.refund is errored",
                    { body, error }
                );
                this.spendingRulesStore.setError(true);
                throw error;
            }),
            tap(() => {
                this.logger.debug(
                    "RewardsSpendingRulesService.refund is ready",
                    body
                );
            })
        );
    }
}

export default RewardsSpendingRulesService;
